/**
 * HouseRakeTile — Street Dice house revenue breakdown for the admin
 * dashboard. Shows today's + lifetime totals split by source:
 *   • Come-out 2/12 rake (half the shooter's matched stake on 2/12)
 *   • Bot winnings routed from real-money human stakes
 * Data source: GET /api/admin/street-dice/rake-summary
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Landmark, RefreshCw, Bot, Dice5 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;

const HouseRakeTile = () => {
    const [data, setData] = useState(null);
    const [hourly, setHourly] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [{ data }, { data: h }] = await Promise.all([
                axios.get(`${API}/admin/street-dice/rake-summary`, { withCredentials: true }),
                axios.get(`${API}/admin/street-dice/rake-hourly?hours=24`, { withCredentials: true }),
            ]);
            setData(data);
            setHourly(h);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Could not load rake summary');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        // Refresh every 60s so the tile stays live during heavy Street Dice traffic
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, [load]);

    const today = data?.breakdown?.today || {};
    const lifetime = data?.breakdown?.lifetime || {};
    const todayRake = today.come_out_rake || { amount: 0, events: 0 };
    const todayBots = today.bot_winnings || { amount: 0, events: 0 };
    const lifeRake = lifetime.come_out_rake || { amount: 0, events: 0 };
    const lifeBots = lifetime.bot_winnings || { amount: 0, events: 0 };
    const todayTotal = todayRake.amount + todayBots.amount;
    const lifeTotal = data?.lifetime ?? (lifeRake.amount + lifeBots.amount);

    return (
        <div
            className="w-full rounded-2xl border p-5"
            style={{
                background: 'linear-gradient(180deg, rgba(255,215,0,0.08) 0%, rgba(255,215,0,0.02) 100%)',
                borderColor: 'rgba(255,215,0,0.3)',
            }}
            data-testid="admin-street-dice-rake-tile"
        >
            <div className="flex items-center gap-2 mb-3">
                <Landmark className="w-5 h-5 text-[#FFD700]" />
                <p className="text-xs uppercase tracking-wider text-[#A0AAB2] font-bold">
                    Street Dice · House Revenue
                </p>
                <button
                    onClick={load}
                    disabled={loading}
                    className="ml-auto p-1 rounded hover:bg-white/5 text-white/50 hover:text-white/90 disabled:opacity-40"
                    aria-label="Refresh rake summary"
                    data-testid="admin-rake-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {error ? (
                <p className="text-xs text-[#FF3B30]">{error}</p>
            ) : (
                <>
                    {/* Top row: two headline totals */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Today (CT)</p>
                            <p
                                className="text-3xl font-black text-white tabular-nums mt-0.5"
                                data-testid="admin-rake-today"
                            >
                                {fmt$(todayTotal)}
                            </p>
                            <p className="text-[10px] text-white/50 mt-0.5">
                                {(todayRake.events || 0) + (todayBots.events || 0)} event
                                {((todayRake.events || 0) + (todayBots.events || 0)) === 1 ? '' : 's'}
                            </p>
                        </div>
                        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
                            <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Lifetime</p>
                            <p
                                className="text-3xl font-black text-[#FFD700] tabular-nums mt-0.5"
                                data-testid="admin-rake-lifetime"
                            >
                                {fmt$(lifeTotal)}
                            </p>
                            <p className="text-[10px] text-white/50 mt-0.5">
                                Booked to house P&amp;L bucket
                            </p>
                        </div>
                    </div>

                    {/* Sparkline — last 24h hourly revenue. Lets operators
                        see the impact of aggressiveness tweaks land within
                        the hour instead of eyeballing yesterday's number. */}
                    <RakeSparkline buckets={hourly?.buckets || []} />

                    {/* Breakdown by source */}
                    <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-2">
                        Where the money came from
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <SourceRow
                            icon={<Dice5 className="w-4 h-4 text-[#FFD700]" />}
                            label="Come-out 2/12 rake"
                            todayAmt={todayRake.amount}
                            todayCnt={todayRake.events}
                            lifeAmt={lifeRake.amount}
                            lifeCnt={lifeRake.events}
                            hint="Half of matched wager on 2/12 come-out craps"
                            testid="admin-rake-source-come-out"
                        />
                        <SourceRow
                            icon={<Bot className="w-4 h-4 text-[#22c55e]" />}
                            label="Bot winnings routed"
                            todayAmt={todayBots.amount}
                            todayCnt={todayBots.events}
                            lifeAmt={lifeBots.amount}
                            lifeCnt={lifeBots.events}
                            hint="Real-money payouts bots won from humans"
                            testid="admin-rake-source-bots"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

const SourceRow = ({ icon, label, todayAmt, todayCnt, lifeAmt, lifeCnt, hint, testid }) => (
    <div
        className="rounded-lg border border-white/10 bg-black/20 p-3"
        data-testid={testid}
    >
        <div className="flex items-center gap-1.5 mb-1">
            {icon}
            <p className="text-xs font-bold text-white">{label}</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
            <div>
                <p className="text-[9px] uppercase tracking-wider text-[#A0AAB2]">Today</p>
                <p className="text-lg font-black text-white tabular-nums">
                    {fmt$(todayAmt)}
                </p>
                <p className="text-[9px] text-white/40">
                    {todayCnt || 0} event{todayCnt === 1 ? '' : 's'}
                </p>
            </div>
            <div>
                <p className="text-[9px] uppercase tracking-wider text-[#A0AAB2]">Lifetime</p>
                <p className="text-lg font-black text-[#FFD700] tabular-nums">
                    {fmt$(lifeAmt)}
                </p>
                <p className="text-[9px] text-white/40">
                    {lifeCnt || 0} event{lifeCnt === 1 ? '' : 's'}
                </p>
            </div>
        </div>
        <p className="text-[10px] text-white/50 mt-2 leading-snug">{hint}</p>
    </div>
);

// ─────────────────────────────────────────────────────────────────────
// RakeSparkline — SVG stacked area chart of hourly rake revenue.
// Bottom band = 2/12 come-out rake; top band = bot winnings routed.
// Draws a full 24-hour axis even if some hours are $0 (so the graph
// tells a story about traffic vs. revenue at a glance).
// ─────────────────────────────────────────────────────────────────────
const RakeSparkline = ({ buckets }) => {
    if (!buckets || buckets.length === 0) return null;
    const W = 480, H = 68, PAD_L = 4, PAD_R = 4, PAD_T = 6, PAD_B = 12;
    const innerW = W - PAD_L - PAD_R;
    const innerH = H - PAD_T - PAD_B;
    const max = Math.max(0.5, ...buckets.map((b) => b.total || 0));
    const step = innerW / Math.max(1, buckets.length - 1);
    const y = (v) => PAD_T + innerH - (v / max) * innerH;

    const rakePts = buckets.map((b, i) => `${PAD_L + i * step},${y(b.come_out_rake || 0)}`).join(' ');
    const totalPts = buckets.map((b, i) => `${PAD_L + i * step},${y(b.total || 0)}`).join(' ');
    const areaRake = `${PAD_L},${PAD_T + innerH} ${rakePts} ${PAD_L + innerW},${PAD_T + innerH}`;
    const areaTotal = `${PAD_L},${PAD_T + innerH} ${totalPts} ${PAD_L + innerW},${PAD_T + innerH}`;
    const now = new Date();

    return (
        <div className="mb-3" data-testid="admin-rake-sparkline">
            <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Last 24h · hourly</p>
                <div className="ml-auto flex items-center gap-3 text-[9px] text-white/60">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#22c55e]/70" /> bots</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FFD700]/70" /> 2/12 rake</span>
                </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-16 rounded-md bg-black/25 border border-white/10">
                {/* Total (bots + rake) — greenish fill */}
                <polygon points={areaTotal} fill="rgba(34,197,94,0.28)" />
                {/* Come-out rake portion drawn on top so it stacks visually */}
                <polygon points={areaRake} fill="rgba(255,215,0,0.55)" />
                <polyline points={totalPts} fill="none" stroke="#22c55e" strokeWidth="1.2" opacity="0.85" />
                <polyline points={rakePts} fill="none" stroke="#FFD700" strokeWidth="1.2" opacity="0.9" />
                {/* Now marker at right edge */}
                <line x1={W - PAD_R} y1={PAD_T} x2={W - PAD_R} y2={H - PAD_B + 2} stroke="rgba(255,255,255,0.25)" strokeDasharray="2 2" />
                <text x={W - PAD_R - 2} y={H - 2} textAnchor="end" fontSize="8" fill="rgba(255,255,255,0.5)">
                    {now.getHours().toString().padStart(2, '0')}:00
                </text>
                <text x={PAD_L + 2} y={H - 2} textAnchor="start" fontSize="8" fill="rgba(255,255,255,0.5)">
                    -24h
                </text>
            </svg>
        </div>
    );
};

export default HouseRakeTile;
